import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory, {
  PaginationProvider,
  PaginationListStandalone,
} from "react-bootstrap-table2-paginator";
import ToolkitProvider, { Search } from "react-bootstrap-table2-toolkit";
import { Card, CardBody, Row, Col, Container } from "reactstrap";
import { MetaTags } from "react-meta-tags";
import BreadcrumbSub from "components/Common/BreadcrumbSub";
import Breadcrumbs from "components/Common/Breadcrumb";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { getSelectedUserAnalyticsDeatils } from "services/appAnalytics.service";
import Spinner from "components/spinner";

export const SelectedUserAnalyticsDetails = () => {
  const { userId } = useParams();
  const { SearchBar } = Search;

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchUserClicks();
  }, []);

  const fetchUserClicks = async () => {
    setLoading(true);
    try {
      const res = await getSelectedUserAnalyticsDeatils(userId);
      if (res?.success) {
        setEvents(res?.data || []);
      }
    } catch (error) {
      console.error("Error fetching click data", error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      dataField: "screenName",
      text: "Screen Name",
      sort: true,
    },
    {
      dataField: "button.name",
      text: "Button Name",
      sort: true,
      formatter: (_, row) => row.button?.name || "NA",
    },
    {
      dataField: "button.id",
      text: "Button ID",
      formatter: (_, row) => {
        const id = row.button?.id || "NA";
        return id.length > 35 ? id.match(/.{1,35}/g).join("\n") : id;
      },
      style: {
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      },
    },
    {
      dataField: "createdAt",
      text: "Timestamp",
      formatter: cell => moment(cell).format("DD-MMM-YYYY hh:mm A"),
    },
  ];

  const pageOptions = {
    sizePerPage: 10,
    totalSize: events.length,
    custom: true,
  };

  const chartRaw = Object.values(
    events.reduce((acc, curr) => {
      const screen = curr.screenName || "Unknown";
      const button = curr.button?.name || "Unnamed Button";
      const key = `${screen} | ${button}`;

      if (!acc[key]) {
        acc[key] = { label: key, count: 1 };
      } else {
        acc[key].count += 1;
      }
      return acc;
    }, {})
  );
  const chartData = chartRaw.map((item, index) => ({
    ...item,
    index: index + 1,
  }));

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>User Analytics Details</title>
        </MetaTags>
        <Container fluid>
          <Breadcrumbs
            title="User Analytics"
            breadcrumbItem="Selected User Analytics"
          />
          <BreadcrumbSub
            subtile="Selected User Analytics"
            // SubBreadcrumbItem="Analytics"
          />

          <Row className="mt-3">
            <Col>
              <Card>
                <CardBody>
                  <PaginationProvider
                    pagination={paginationFactory(pageOptions)}
                  >
                    {({ paginationProps, paginationTableProps }) => (
                      <ToolkitProvider
                        keyField="_id"
                        data={events}
                        columns={columns}
                        search
                      >
                        {toolkitProps => (
                          <>
                            <Row className="mb-2">
                              <Col md="12">
                                <SearchBar
                                  {...toolkitProps.searchProps}
                                  placeholder="Search events..."
                                />
                              </Col>
                            </Row>

                            <div className="table-responsive">
                              <BootstrapTable
                                keyField="_id"
                                bordered={false}
                                striped
                                noDataIndication={
                                  loading
                                    ? "Loading..."
                                    : "No Click Events Found"
                                }
                                {...toolkitProps.baseProps}
                                {...paginationTableProps}
                              />
                            </div>

                            <Row className="mt-3">
                              <Col className="d-flex justify-content-end">
                                <PaginationListStandalone
                                  {...paginationProps}
                                />
                              </Col>
                            </Row>
                          </>
                        )}
                      </ToolkitProvider>
                    )}
                  </PaginationProvider>
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Row className="mt-4">
            <Col md="9">
              <Card>
                <CardBody>
                  <h5 className="mb-4">Clicks by Screen & Button</h5>
                  {chartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart
                        data={chartData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 50 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="index"
                          label={{
                            value: "Index",
                            position: "insideBottom",
                            dy: 10,
                          }}
                          tick={{ fontSize: 12 }}
                        />
                        <YAxis allowDecimals={false} />
                        <Tooltip
                          formatter={(value, name, props) => [
                            value,
                            chartData[props.payload.index - 1]?.label,
                          ]}
                        />
                        <Legend />
                        <Bar dataKey="count" fill="#8884d8" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-center">No Data for Chart</p>
                  )}
                </CardBody>
              </Card>
            </Col>

            <Col md="3">
              <Card>
                <CardBody>
                  <h6 className="mb-3">Index Legend</h6>
                  <ul className="list-unstyled">
                    {chartData.map((item, i) => (
                      <li key={i}>
                        <strong>{item.index}.</strong> {item.label}
                      </li>
                    ))}
                  </ul>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <Spinner loading={loading} />
    </React.Fragment>
  );
};
